/**
 * Currency / language dropdowns (snippets/sky-localization.liquid).
 * The panel scales in from its corner like Sky's localization popover, closes on an outside
 * click or Escape, and the country list can be filtered.
 */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @param {string} value */
const normalize = (value) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

/** @type {WeakMap<HTMLDetailsElement, Animation>} */
const animations = new WeakMap();

/** @param {HTMLDetailsElement} details */
function popoverOf(details) {
  return /** @type {HTMLElement | null} */ (details.querySelector('.sky-localization__popover'));
}

/** @param {HTMLDetailsElement} details */
function open(details) {
  document.querySelectorAll('.sky-localization__item[open]').forEach((other) => {
    if (other !== details) close(/** @type {HTMLDetailsElement} */ (other));
  });
  animations.get(details)?.cancel();
  details.classList.remove('is-closing');
  details.open = true;
  const popover = popoverOf(details);
  if (!popover || reducedMotion()) return;
  animations.set(
    details,
    popover.animate([{ transform: 'scale(0)' }, { transform: 'scale(1)' }], {
      duration: 200,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    })
  );
}

/** @param {HTMLDetailsElement} details */
function close(details) {
  if (!details.open || details.classList.contains('is-closing')) return;
  animations.get(details)?.cancel();
  const popover = popoverOf(details);
  if (!popover || reducedMotion()) {
    details.open = false;
    return;
  }
  details.classList.add('is-closing');
  const animation = popover.animate([{ transform: 'scale(1)' }, { transform: 'scale(0)' }], {
    duration: 160,
    easing: 'cubic-bezier(0.4, 0, 1, 1)',
    fill: 'forwards',
  });
  animations.set(details, animation);
  animation.onfinish = () => {
    details.open = false;
    details.classList.remove('is-closing');
    animation.cancel();
  };
}

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const summary = target.closest('.sky-localization__item > summary');
  if (summary) {
    event.preventDefault();
    const details = /** @type {HTMLDetailsElement} */ (summary.parentElement);
    if (details.open && !details.classList.contains('is-closing')) close(details);
    else open(details);
    return;
  }
  document.querySelectorAll('.sky-localization__item[open]').forEach((details) => {
    if (!details.contains(target)) close(/** @type {HTMLDetailsElement} */ (details));
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.sky-localization__item[open]').forEach((details) => {
    close(/** @type {HTMLDetailsElement} */ (details));
    details.querySelector('summary')?.focus();
  });
});

document.addEventListener('input', (event) => {
  const input = /** @type {HTMLInputElement} */ (event.target);
  if (!input.matches('[data-sky-loc-search]')) return;
  const popover = input.closest('.sky-localization__popover');
  if (!popover) return;
  const keyword = normalize(input.value);
  let visible = 0;
  popover.querySelectorAll('[data-country]').forEach((item) => {
    const hidden = !!keyword && !normalize(/** @type {HTMLElement} */ (item).dataset.country || '').includes(keyword);
    /** @type {HTMLElement} */ (item).hidden = hidden;
    if (!hidden) visible += 1;
  });
  popover.querySelector('.sky-localization__empty')?.toggleAttribute('hidden', visible > 0);
});

// Enter in the filter field must not submit the form (it would pick the first country).
document.addEventListener('keydown', (event) => {
  const input = /** @type {HTMLElement} */ (event.target);
  if (event.key === 'Enter' && input.matches?.('[data-sky-loc-search]')) event.preventDefault();
});
